/**
 * Service-worker registration (build plan §7).
 *
 * The worker itself is `public/sw.js` — plain JS, copied verbatim into `dist/`
 * by Vite and served no-cache at the site root (docker/shell/nginx.conf.template
 * and the CloudFront behaviour), so its scope is the whole origin and a deploy
 * always ships a fresh copy.
 *
 * Why it exists at all:
 *  1. Chromium will not treat an app as installable without a worker that has
 *     a `fetch` handler.
 *  2. Offline tolerance for the shell itself — a cached `index.html` means a
 *     launched-from-the-homescreen app on a dead connection shows the shell
 *     rather than the browser's dinosaur.
 *
 * 🔴 **THE QUERY STRING IS GONE — IT HAD ALREADY STOPPED DECIDING ANYTHING** (D118, PNK-121).
 *
 * The worker was registered as `/sw.js?tenant=<slug>&resolve=<url>`, because a fetch handler must
 * decide synchronously and so read its policy off its own script URL: which tenant this was, and the
 * exact resolve URL to serve cache-first. `public/sw.js` stopped reading either when the offline
 * policy went (below), and D118 removed the two values themselves — there is one app, and no resolve
 * call to cache. So the registration is `/sw.js`, flat.
 *
 * A scope has ONE registration, so registering a different script URL at `/` replaces the previous
 * one rather than adding to it; an app already installed re-registers once and keeps its caches,
 * which `KEEP` in `sw.js` is what guarantees.
 *
 * 🔴 **THE `&offline=1` HALF WENT FIRST, AND WITH IT THE THIRD REASON** (D115 Stage 0, 2026-09-13).
 *
 * There used to be a third: "for the passport, the whole app with no signal (D6, D9)". A tenant
 * whose slug was `passport` got `&offline=1`, and `public/sw.js` answered it by precaching the
 * tenant's modules and serving the resolve answer cache-first. PNK-106 deleted that app and
 * migration 044 deleted its `tenantApps` row, so no resolve has been able to answer with that slug
 * since — `tenantApps` holds exactly one row, `desk`. The consumer in `sw.js` was deleted earlier in
 * this branch; this is the producer, which had been setting a flag for a tenant that cannot exist
 * and exporting a wait nothing could reach.
 *
 * Offline returns in D115 Stage 3, against the content-hashed manifest.
 */
export const SERVICE_WORKER_PATH = "/sw.js";

export interface ServiceWorkerOptions {
  /**
   * The install identity, still passed and still unused by the URL — `boot.ts` has it, and keeping
   * it in the signature is what makes the next offline policy a change to THIS file rather than to
   * its four callers. `src/app/manifest.ts` explains why the value itself must not move.
   */
  slug: string | null;
}

/** The registration URL. No query string: `public/sw.js` reads nothing from one. */
export function serviceWorkerUrl(_options: ServiceWorkerOptions = { slug: null }): string {
  return SERVICE_WORKER_PATH;
}

function container(): ServiceWorkerContainer | null {
  return "serviceWorker" in navigator && navigator.serviceWorker ? navigator.serviceWorker : null;
}

export async function registerServiceWorker(options: ServiceWorkerOptions): Promise<ServiceWorkerRegistration | null> {
  const workers = container();
  if (!workers) return null;
  // A worker registered from `vite dev` would cache the dev server's module
  // graph and shadow every subsequent edit.
  if (!import.meta.env.PROD) return null;
  try {
    return await workers.register(serviceWorkerUrl(options), { scope: "/" });
  } catch {
    // A blocked or unsupported registration costs the install prompt, nothing else.
    return null;
  }
}

